import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Login = () => {
    // Función para limpiar siempre la caché de imágenes
    const [cacheBuster] = useState(() => Date.now());
    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const { login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [loginInput, setLoginInput] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('landing'); // 'landing' or 'login'
    const navigate = useNavigate();
    const location = useLocation();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const result = await login(loginInput);
        if (result.success) {
            try {
                await api.post('/sessions/log', {
                    page_name: 'login',
                    action_type: 'login'
                });
            } catch (err) {
                console.error("Failed to log login action", err);
            }
            // Dynamic redirection: restore state.from (with pathname & search) or fallback to '/'
            const fromState = location.state?.from;
            let targetPath = '/';
            if (fromState) {
                targetPath = `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
            }
            navigate(targetPath, { replace: true });
        } else {
            setError('Valide su información, si no se encuentra registrado, lo invitamos a registrarse.');
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const pageDesign = config?.pageDesigns?.login || {};

    const isRegisterEnabled = (config?.pages?.showRegisterButton === true || config?.pages?.register === true) &&
        config?.pages?.showRegisterButton !== false &&
        config?.pages?.register !== false;

    // Priorizar imagen de fondo por sección (pageDesign.bgImage) y luego fondo global
    const sectionBg = pageDesign?.bgImage ? getCleanImageUrl(pageDesign.bgImage) : '';
    const globalBg = isMobile
        ? (config?.backgroundMobile || config?.backgroundImage ? getCleanImageUrl(config.backgroundMobile || config.backgroundImage) : '')
        : (config?.backgroundDesktop || config?.backgroundImage ? getCleanImageUrl(config.backgroundDesktop || config.backgroundImage) : '');
    const bottomBg = sectionBg || globalBg;

    const logoUrl = config?.logoUrl ? getCleanImageUrl(config.logoUrl) : '';
    const primaryColor = config?.primaryColor || "#0a58ca";
    const secondaryColor = config?.secondaryColor || "#079acf";

    const containerStyle = {
        backgroundColor: pageDesign.backgroundColor || config?.backgroundColor || '#ffffff',
        color: pageDesign.globalFontColor || config?.globalFontColor || '#000000'
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col relative overflow-x-hidden items-center"
        >
            {/* Global Background Layer */}
            {bottomBg && (
                <div className="absolute inset-0 pointer-events-none z-0">
                    <img
                        src={bottomBg}
                        alt="Fondo Global"
                        className="w-full h-full object-cover object-bottom"
                    />
                </div>
            )}

            {/* Reusable Responsive Header */}
            <Header pageKey="login" />

            {/* Content Area */}
            <div className="w-full max-w-sm px-6 z-10 flex flex-col justify-center flex-grow my-4 animate-fadeIn">
                {isRegisterEnabled && viewMode === 'landing' ? (
                    <div className="flex flex-col gap-5 w-full">
                        <button
                            onClick={() => setViewMode('login')}
                            style={{ backgroundColor: primaryColor }}
                            className="w-full text-white py-4 px-6 rounded-md font-black uppercase tracking-widest text-[14px] sm:text-[16px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            INICIAR
                        </button>

                        <button
                            onClick={() => navigate('/register', { state: location.state })}
                            style={{ backgroundColor: secondaryColor }}
                            className="w-full text-white py-4 px-6 rounded-md font-black uppercase tracking-widest text-[14px] sm:text-[16px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            REGISTRARSE
                        </button>
                    </div>
                ) : (
                    <div
                        style={{ borderColor: primaryColor }}
                        className="w-full bg-white/90 backdrop-blur-sm border-2 p-6 sm:p-8 rounded-3xl shadow-[0_15px_35px_rgba(0,0,0,0.15)] relative animate-fadeIn"
                    >
                        <div className="text-center mb-6">
                            <h2 style={{ color: primaryColor }} className="text-2xl font-black uppercase tracking-wider mb-2">
                                INICIO DE SESIÓN
                            </h2>
                            <p className="text-xs font-semibold text-slate-600 leading-relaxed">
                                Ingresa tu correo de Boehringer Ingelheim para iniciar sesión.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                            <div className="flex flex-col gap-1.5 text-left">
                                <label style={{ color: primaryColor }} className="text-[13px] font-bold">
                                    Correo Electrónico*
                                </label>
                                <input
                                    type="text"
                                    value={loginInput}
                                    onChange={(e) => setLoginInput(e.target.value)}
                                    placeholder="Ingresa tu correo"
                                    required
                                    style={{ borderColor: primaryColor }}
                                    className="w-full bg-white border-[1.5px] p-3 rounded-md focus:outline-none transition-all font-bold text-slate-700 shadow-sm text-sm"
                                />
                            </div>

                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-xs font-bold text-center border border-red-200">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{ backgroundColor: primaryColor }}
                                className="w-full text-white py-3.5 px-6 rounded-md font-black uppercase tracking-widest text-[14px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center disabled:opacity-50 mt-2"
                            >
                                {isLoading ? 'INGRESANDO...' : 'INGRESAR'}
                            </button>

                            {isRegisterEnabled && (
                                <button
                                    type="button"
                                    onClick={() => setViewMode('landing')}
                                    style={{ color: primaryColor }}
                                    className="text-xs font-bold transition-colors mt-2 text-center hover:underline"
                                >
                                    ← Volver
                                </button>
                            )}
                        </form>
                    </div>
                )}
            </div>

            {/* Reusable Responsive Footer */}
            <Footer pageKey="login" />
        </div>
    );
};

export default Login;
